import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { COMPANY, NAV } from "@/constants/company";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`header-enter fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "backdrop-blur-xl bg-sage-50/90 border-b border-sage-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="header-logo"
          className="flex items-center transition-transform duration-300 ease-out hover:scale-[1.03] active:scale-100"
          aria-label="Rossi Energia home"
        >
          <img
            src={COMPANY.logo}
            alt="Rossi Energia"
            className="h-16 w-auto object-contain"
          />
        </button>

        {/* Desktop nav: sempre visibile da 1024px, indipendente da qualsiasi animazione JS */}
        <nav
          data-testid="desktop-nav"
          className="relative hidden lg:flex lg:visible lg:opacity-100 items-center gap-9"
        >
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => scrollTo(n.href)}
              data-testid={`nav-${n.href.replace("#", "")}`}
              className="text-sm font-medium text-forest-700 hover:text-forest-900 transition-colors relative group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-forest-900 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollTo("#contatti")}
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center rounded-full bg-forest-900 px-6 py-3 text-sm font-medium text-sage-50 hover:bg-forest-700 transition-[background-color,transform] duration-300 ease-out hover:-translate-y-0.5 active:translate-y-0"
          >
            Valuta la bolletta
          </button>

          {/* Hamburger: visibile sotto 1024px */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full text-forest-900 hover:bg-sage-200/60 transition-colors"
          >
            {open ? <X className="h-6 w-6" strokeWidth={1.8} /> : <Menu className="h-6 w-6" strokeWidth={1.8} />}
          </button>
        </div>
      </div>

      {/* Menu mobile (sotto 1024px) */}
      <div
        data-testid="mobile-nav"
        className={`lg:hidden overflow-hidden border-t border-sage-200/60 bg-sage-50/95 backdrop-blur-xl transition-[max-height,opacity] duration-300 ease-out ${
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              data-testid={`mobile-nav-${n.href.replace("#", "")}`}
              className="text-left py-3 text-base font-medium text-forest-800 hover:text-forest-900 transition-colors border-b border-sage-200/50 last:border-0"
            >
              {n.label}
            </button>
          ))}
          <button
            onClick={() => go("#contatti")}
            data-testid="mobile-cta"
            className="mt-4 inline-flex items-center justify-center rounded-full bg-forest-900 px-6 py-3 text-sm font-medium text-sage-50 hover:bg-forest-700 transition-colors"
          >
            Valuta la bolletta
          </button>
        </nav>
      </div>
    </header>
  );
}
